import { find, forEach, get, isEmpty, map, sortBy, uniq } from 'lodash';

import { ThresholdJob } from 'interfaces/job';
import { Thresholds } from 'interfaces/jobs/multiplex/cellTypingParams';

export interface ThresholdSourceOption {
  marker: string;
  threshold: number;
  jobId: string;
  jobName?: string;
  orchestrationId?: string | null;
}

export interface ThresholdConflict {
  marker: string;
  options: ThresholdSourceOption[];
  resolvedJobId?: string;
}

export interface ThresholdPreviewRow {
  marker: string;
  threshold?: number;
  sourceJobId?: string;
  sourceJobName?: string;
  sourceOrchestrationId?: string | null;
  isConflict: boolean;
  isResolved: boolean;
  options: ThresholdSourceOption[];
}

export interface InvalidThresholdJobSelection {
  jobId: string;
  jobName?: string;
  reason: 'missing-job-id' | 'missing-thresholds';
}

export type ThresholdConflictResolutions = Record<string, string>;

export interface ThresholdMergeState {
  thresholds: Thresholds;
  previewRows: ThresholdPreviewRow[];
  conflicts: ThresholdConflict[];
  unresolvedConflictMarkers: string[];
  invalidJobs: InvalidThresholdJobSelection[];
  perMarkerThresholdsOrchestrationId: string | null;
  selectedSourceJobIds: string[];
  hasBlockingIssues: boolean;
}

const getThresholdJobSourceId = (job: ThresholdJob): string => job.id || job.orchestrationId || '';

const getJobName = (job: ThresholdJob): string | undefined => job.name || job.orchestrationId || job.id;

const getSelectedOption = (
  options: ThresholdSourceOption[],
  conflictResolutions: ThresholdConflictResolutions,
  marker: string
): ThresholdSourceOption | undefined => {
  if (options.length === 1) {
    return options[0];
  }

  const resolvedJobId = conflictResolutions[marker];
  return resolvedJobId ? find(options, { jobId: resolvedJobId }) : undefined;
};

export const buildThresholdMergeState = (
  selectedJobs: ThresholdJob[],
  conflictResolutions: ThresholdConflictResolutions = {}
): ThresholdMergeState => {
  const optionsByMarker: Record<string, ThresholdSourceOption[]> = {};
  const invalidJobs: InvalidThresholdJobSelection[] = [];

  forEach(selectedJobs, (job) => {
    const jobId = getThresholdJobSourceId(job);
    const jobName = getJobName(job);

    if (!jobId) {
      invalidJobs.push({ jobId: '', jobName, reason: 'missing-job-id' });
      return;
    }

    const thresholds = get(job, 'results.perMarkerThresholds') as Thresholds | undefined;

    if (isEmpty(thresholds)) {
      invalidJobs.push({ jobId, jobName, reason: 'missing-thresholds' });
      return;
    }

    forEach(thresholds, (threshold, marker) => {
      optionsByMarker[marker] = [
        ...(optionsByMarker[marker] || []),
        {
          marker,
          threshold,
          jobId,
          jobName,
          orchestrationId: job.orchestrationId ?? null,
        },
      ];
    });
  });

  const thresholds: Thresholds = {};
  const selectedSourceJobIds: string[] = [];
  const previewRows: ThresholdPreviewRow[] = [];
  const conflicts: ThresholdConflict[] = [];
  const unresolvedConflictMarkers: string[] = [];

  forEach(sortBy(map(optionsByMarker, (_options, marker) => marker)), (marker) => {
    const options = optionsByMarker[marker];
    const selectedOption = getSelectedOption(options, conflictResolutions, marker);
    const isConflict = options.length > 1;
    const isResolved = Boolean(selectedOption);

    if (selectedOption) {
      thresholds[marker] = selectedOption.threshold;
      selectedSourceJobIds.push(selectedOption.jobId);
    }

    if (isConflict) {
      conflicts.push({ marker, options, resolvedJobId: selectedOption?.jobId });
      if (!selectedOption) {
        unresolvedConflictMarkers.push(marker);
      }
    }

    previewRows.push({
      marker,
      threshold: selectedOption?.threshold,
      sourceJobId: selectedOption?.jobId,
      sourceJobName: selectedOption?.jobName,
      sourceOrchestrationId: selectedOption?.orchestrationId,
      isConflict,
      isResolved,
      options,
    });
  });

  const uniqueSelectedSourceJobIds = uniq(selectedSourceJobIds);
  const singleSourceJobId = uniqueSelectedSourceJobIds.length === 1 ? uniqueSelectedSourceJobIds[0] : null;
  const singleSourceJob = singleSourceJobId
    ? find(selectedJobs, (job) => getThresholdJobSourceId(job) === singleSourceJobId)
    : undefined;

  return {
    thresholds,
    previewRows,
    conflicts,
    unresolvedConflictMarkers,
    invalidJobs,
    perMarkerThresholdsOrchestrationId: singleSourceJob?.orchestrationId ?? null,
    selectedSourceJobIds: uniqueSelectedSourceJobIds,
    hasBlockingIssues: !isEmpty(unresolvedConflictMarkers) || !isEmpty(invalidJobs),
  };
};
